/**
 * /onboarding — guided first-run experience, styled after the brand slide
 * mockups: clean white slides where Resmo introduces himself, then setup
 * questions asked one at a time, then "How do you want to start?" (A–E),
 * then a tour offer. Skippable at every step.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ResmoMascot from "../components/ResmoMascot";
import FirstImpressionCard from "../components/FirstImpressionCard";
import {
  ArrowRight, ArrowLeft, Upload, ClipboardPaste, Loader, Check, Sparkles, Map,
} from "lucide-react";

const NAVY = "#0F1B2D";
const ORANGE = "#C2410C";

const JOURNEY_STAGES = [
  { id: "applying", label: "Actively applying" },
  { id: "exploring", label: "Exploring options" },
  { id: "curious", label: "Happy but curious" },
  { id: "interviewing", label: "Prepping for interviews" },
];

const START_OPTIONS = [
  { id: "build_resume", letter: "A", pre: "Tailor or create a ", hi: "resume", post: "", route: "/studio" },
  { id: "skills_worth", letter: "B", pre: "See what your ", hi: "skills", post: " are worth?", route: "/skills" },
  { id: "interview_prep", letter: "C", pre: "Practice ", hi: "interviewing", post: " for a job?", route: "/playbooks" },
  { id: "job_search", letter: "D", pre: "Look for a ", hi: "job", post: "", route: "/job-search" },
  { id: "something_else", letter: "E", pre: "", hi: "Do something else? Let me know!", post: "" },
];

const MORE_OPTIONS = [
  { id: "feed_resume", title: "Feed me your resume", sub: "Upload or paste it — I'll share my first impression", inline: true },
  { id: "decode_jd", title: "Decode a job description", sub: "Learn the TRUE problem the role is solving for", route: "/feed-job" },
  { id: "research_role", title: "Research a role title", sub: "See what a role really demands before you chase it", route: "/skills/role/new" },
  { id: "networking", title: "Find networking events", sub: "Upcoming events + your elevator speech creator", route: "/network" },
];

const COLLAGE_BUBBLES = [
  "Assessing your skills & their value",
  "Finding a job and role research",
  "Network build & elevator speech developer",
  "Interview prep & practice",
];

const StepDots = ({ step }) => (
  <div className="flex items-center gap-2" data-testid="onboarding-dots">
    {[0, 1, 2, 3].map((i) => (
      <span key={i} className="rounded-full transition-all"
            style={{ width: i === step ? 22 : 8, height: 8,
                     background: i <= step ? ORANGE : "#E2E8F0" }} />
    ))}
  </div>
);

const ThoughtBubble = ({ text, side = "right" }) => (
  <div className={`flex items-center gap-1.5 ${side === "left" ? "flex-row-reverse" : ""}`}>
    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "#CBD5E1" }} />
    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: "#E2E8F0", border: "1.5px solid #CBD5E1" }} />
    <span className="px-3 py-2 text-[11px] font-semibold leading-snug text-center rounded-[18px]"
          style={{ background: "white", border: "1.5px solid #CBD5E1", color: NAVY, maxWidth: 150,
                   boxShadow: "0 2px 6px rgba(15,27,45,.06)" }}>
      {text}
    </span>
  </div>
);

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = (user?.name || "there").split(" ")[0];

  const [step, setStep] = useState(0);
  const [slide, setSlide] = useState(0);
  const [qIndex, setQIndex] = useState(0);
  const [journey, setJourney] = useState("");
  const [city, setCity] = useState("");
  const [remote, setRemote] = useState("any");
  const [salary, setSalary] = useState("");
  const [goal, setGoal] = useState("");
  const [goalRoute, setGoalRoute] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeMode, setResumeMode] = useState("upload");
  const [pasted, setPasted] = useState("");
  const [chewing, setChewing] = useState(false);
  const [resumeDone, setResumeDone] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/onboarding/config")
      .then((r) => { if (r.data?.enabled === false) navigate("/welcome", { replace: true }); })
      .catch(() => {});
  }, [navigate]);

  // Intro slides auto-advance; clicking advances immediately.
  useEffect(() => {
    if (step !== 0) return undefined;
    const t = window.setTimeout(() => {
      if (slide < 2) setSlide(slide + 1);
      else setStep(1);
    }, 6500);
    return () => window.clearTimeout(t);
  }, [step, slide]);

  const nextSlide = () => (slide < 2 ? setSlide(slide + 1) : setStep(1));

  const complete = async (extra = {}) => {
    try {
      await api.post("/onboarding/complete", { journey_stage: journey, goal, ...extra });
    } catch { /* non-blocking */ }
  };

  const skip = async () => {
    await complete({ skipped: true });
    navigate("/welcome");
  };

  const savePrefs = () => {
    api.patch("/job-prefs", {
      location: city.trim(),
      remote,
      salary_min: salary ? parseInt(salary, 10) : 0,
    }).catch(() => {});
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(""); setChewing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/resume/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTimeout(() => { setChewing(false); setResumeDone(true); }, 1200);
    } catch (err) {
      setChewing(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onPaste = async () => {
    if (pasted.trim().length < 30) { setError("That looks too short for a resume."); return; }
    setError(""); setChewing(true);
    try {
      await api.post("/resume/text", { content: pasted });
      setTimeout(() => { setChewing(false); setResumeDone(true); }, 1200);
    } catch (err) {
      setChewing(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const pickStart = (opt) => {
    setGoal(opt.id);
    setGoalRoute(opt.route || "");
    if (opt.id === "something_else") { setMoreOpen(true); return; }
    setMoreOpen(false); setResumeOpen(false);
  };

  const pickMore = (opt) => {
    setGoal(opt.id);
    setGoalRoute(opt.route || "");
    if (opt.inline) setResumeOpen(true);
  };

  const finish = async (wantsTour) => {
    await complete({ wants_tour: wantsTour });
    if (wantsTour) {
      try { localStorage.setItem("resmo:welcome_tour:pending", "1"); } catch { /* ignore */ }
      navigate("/welcome");
      return;
    }
    navigate(goal === "feed_resume" || !goalRoute ? "/" : goalRoute);
  };

  const card = { background: "white", border: "1px solid #E2E8F0", borderRadius: 16,
                 boxShadow: "0 8px 30px rgba(15,27,45,.08)" };

  return (
    <div className="min-h-screen flex items-start justify-center px-4 py-8" style={{ background: "white" }}
         data-testid="onboarding-page">
      <style>{`
        @keyframes resmo-walk-in {
          0% { transform: translateX(-170%) translateY(0) rotate(-6deg); opacity: 0; }
          25% { transform: translateX(-115%) translateY(-8px) rotate(3deg); opacity: 1; }
          45% { transform: translateX(-72%) translateY(0) rotate(-3deg); }
          65% { transform: translateX(-36%) translateY(-8px) rotate(3deg); }
          85% { transform: translateX(-9%) translateY(0) rotate(-2deg); }
          100% { transform: translateX(0) translateY(0) rotate(0); }
        }
        .resmo-walk-in { animation: resmo-walk-in 2s cubic-bezier(.3,1,.4,1) both; }
        @keyframes rm-fade-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .rm-fade-up { animation: rm-fade-up .7s ease 1.5s both; }
        .rm-fade-up-fast { animation: rm-fade-up .5s ease .25s both; }
        @keyframes rm-pop { 0% { transform: scale(.9); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
        .rm-pop { animation: rm-pop .6s cubic-bezier(.34,1.56,.64,1) 1.1s both; }
        .rm-pop-fast { animation: rm-pop .45s cubic-bezier(.34,1.56,.64,1) .1s both; }
        @keyframes ob-fade { from { opacity: 0; } to { opacity: 1; } }
        .ob-fade-1 { animation: ob-fade 1.1s ease .4s both; }
        .ob-fade-2 { animation: ob-fade 1.1s ease 1.7s both; }
        .ob-fade-3 { animation: ob-fade 1.1s ease 3s both; }
      `}</style>

      <div className="w-full max-w-4xl">
        <div className="flex items-center justify-between mb-4">
          <StepDots step={step} />
          <button onClick={skip} className="text-xs font-bold hover:underline"
                  style={{ color: "#94A3B8", background: "none", border: "none", cursor: "pointer" }}
                  data-testid="onboarding-skip-btn">
            Skip for now →
          </button>
        </div>

        {/* STEP 0 — Intro slides on a clean white canvas */}
        {step === 0 && (
          <div onClick={nextSlide} className="cursor-pointer select-none" data-testid="onboarding-step-greet"
               style={{ minHeight: 420 }}>
            {slide === 0 && (
              <div key="s0" className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10 py-10"
                   data-testid="onboarding-slide-hello">
                <div className="ob-fade-1 text-2xl md:text-4xl font-semibold text-center md:text-right"
                     style={{ color: NAVY }}>
                  Hello <span style={{ color: ORANGE }}>{firstName}</span>!&nbsp; Welcome!!
                </div>
                <div className="ob-fade-2">
                  <img src="/brand/resmo-welcoming.png" alt="Resmo welcoming you"
                       style={{ height: 240 }} draggable={false} />
                </div>
                <div className="ob-fade-3 text-2xl md:text-4xl font-semibold text-center md:text-left mt-2 md:mt-16"
                     style={{ color: NAVY }}>
                  My name is <span style={{ color: ORANGE }}>R</span>esmo!
                </div>
              </div>
            )}

            {slide === 1 && (
              <div key="s1" className="flex flex-col md:flex-row items-center justify-center gap-8 py-10"
                   data-testid="onboarding-slide-next-move">
                <div className="ob-fade-1">
                  <img src="/brand/resmo-cheering.png" alt="Resmo cheering"
                       style={{ height: 250 }} draggable={false} />
                </div>
                <div className="ob-fade-2 text-2xl md:text-4xl font-medium text-center md:text-left"
                     style={{ color: "#1E293B", maxWidth: 520 }}>
                  I want to help you <span style={{ color: ORANGE }}>make</span> your next move
                </div>
              </div>
            )}

            {slide === 2 && (
              <div key="s2" className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center py-6"
                   data-testid="onboarding-slide-guide">
                <div className="ob-fade-1 flex flex-col gap-5 items-center">
                  <div className="flex items-end gap-1">
                    <img src="/brand/resmo-thinking.png" alt="" style={{ height: 96 }} draggable={false} />
                    <ThoughtBubble text={COLLAGE_BUBBLES[0]} />
                  </div>
                  <div className="flex items-end gap-1 flex-row-reverse">
                    <img src="/brand/resmo-thinking.png" alt="" style={{ height: 96, transform: "scaleX(-1)" }} draggable={false} />
                    <ThoughtBubble text={COLLAGE_BUBBLES[1]} side="left" />
                  </div>
                  <div className="flex items-end gap-1">
                    <img src="/brand/resmo-character.png" alt="" style={{ height: 96 }} draggable={false} />
                    <ThoughtBubble text={COLLAGE_BUBBLES[2]} />
                  </div>
                </div>
                <div className="ob-fade-2 flex flex-col items-center">
                  <ThoughtBubble text="Resume help — build or tailor, ATS readiness" />
                  <img src="/brand/resmo-delivering.png" alt="Resmo with a tailored resume"
                       style={{ height: 220 }} draggable={false} />
                </div>
                <div className="ob-fade-3 flex flex-col gap-5 items-center">
                  <div className="flex items-end gap-1 flex-row-reverse">
                    <img src="/brand/resmo-thinking.png" alt="" style={{ height: 96, transform: "scaleX(-1)" }} draggable={false} />
                    <ThoughtBubble text={COLLAGE_BUBBLES[3]} side="left" />
                  </div>
                  <div className="p-5 rounded-lg text-center md:text-left"
                       style={{ background: "#3F3F46", color: "white" }}>
                    <div className="text-lg md:text-xl font-medium leading-snug">
                      I am here to help guide you to the right place to get started…
                    </div>
                    <div className="mt-4 mx-auto md:mx-0" style={{ width: 120, height: 1, background: "#A1A1AA" }} />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-center mt-2">
              <button onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                      className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full"
                      style={{ background: "white", border: "1.5px solid #E2E8F0", color: "#64748B" }}
                      data-testid="onboarding-start-btn">
                {slide < 2 ? "Next" : "Let's get set up"} <ArrowRight size={13} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 1 — Setup questions, one at a time, on a clean white box */}
        {step === 1 && (
          <div className="max-w-2xl mx-auto p-6 md:p-8" style={card} data-testid="onboarding-step-questions">
            <div className="flex items-end gap-3 mb-5 overflow-hidden">
              <div className={qIndex === 0 ? "resmo-walk-in flex-shrink-0" : "flex-shrink-0"}>
                <ResmoMascot state="default" size={104} />
              </div>
              <div key={`bubble-${qIndex}`}
                   className={`relative px-4 py-3 mb-4 ${qIndex === 0 ? "rm-pop" : "rm-pop-fast"}`}
                   style={{ background: "white", border: `2px solid ${NAVY}`, borderRadius: 14,
                            boxShadow: `3px 3px 0 0 ${ORANGE}`, maxWidth: 340 }}
                   data-testid="onboarding-question-bubble">
                <span className="font-mono-rm text-[12px] font-bold leading-snug" style={{ color: NAVY }}>
                  {qIndex === 0
                    ? `Nice to meet you, ${firstName}! First — where are you in your journey?`
                    : "Love it. And where do you want to work? This powers your AI job matches from day one."}
                </span>
                <span className="absolute -left-[7px] bottom-4 w-3 h-3 rotate-45"
                      style={{ background: "white", borderLeft: `2px solid ${NAVY}`, borderBottom: `2px solid ${NAVY}` }} />
              </div>
            </div>

            {qIndex === 0 && (
              <div className="flex flex-wrap gap-2 mb-6 rm-fade-up" data-testid="onboarding-q1">
                {JOURNEY_STAGES.map((s) => (
                  <button key={s.id}
                          onClick={() => {
                            setJourney(s.id);
                            window.setTimeout(() => setQIndex(1), 700);
                          }}
                          className="rm-btn text-xs px-3 py-2"
                          style={{ background: journey === s.id ? NAVY : "white",
                                   color: journey === s.id ? "white" : NAVY }}
                          data-testid={`onboarding-journey-${s.id}`}>
                    {journey === s.id && <Check size={12} />} {s.label}
                  </button>
                ))}
              </div>
            )}

            {qIndex === 1 && (
              <div className="rm-fade-up-fast" data-testid="onboarding-q2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-6">
                  <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City (blank = anywhere)"
                         className="rm-input text-sm" data-testid="onboarding-city" />
                  <select value={remote} onChange={(e) => setRemote(e.target.value)}
                          className="rm-input text-sm" data-testid="onboarding-remote">
                    <option value="any">Any setup</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="onsite">On-site</option>
                  </select>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: "#475569" }}>$</span>
                    <input value={salary} onChange={(e) => setSalary(e.target.value.replace(/\D/g, ""))}
                           placeholder="Min salary" inputMode="numeric"
                           className="rm-input text-sm pl-7 w-full" data-testid="onboarding-salary" />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <button onClick={() => (qIndex === 1 ? setQIndex(0) : setStep(0))}
                      className="rm-btn rm-btn-ghost text-xs" data-testid="onboarding-back-1">
                <ArrowLeft size={14} /> Back
              </button>
              {qIndex === 1 && (
                <button onClick={() => { savePrefs(); setStep(2); }} className="rm-btn rm-btn-primary text-sm"
                        data-testid="onboarding-continue-1">
                  Continue <ArrowRight size={14} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* STEP 2 — "How do you want to start?" A–E picker */}
        {step === 2 && (
          <div className="max-w-3xl mx-auto p-6 md:p-10" style={card} data-testid="onboarding-step-quickstart">
            <h2 className="text-2xl md:text-3xl font-semibold mb-6" style={{ color: NAVY }}>
              How do you want to start?&nbsp; Would you like to…
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
              <div className="flex flex-col gap-2.5">
                {START_OPTIONS.map((opt) => {
                  const selected = goal === opt.id || (opt.id === "something_else" && moreOpen);
                  return (
                    <button key={opt.id} onClick={() => pickStart(opt)}
                            className="flex items-baseline gap-4 text-left px-4 py-3 rounded-xl transition-colors"
                            style={{ background: selected ? "#FFF7ED" : "white",
                                     border: `2px solid ${selected ? ORANGE : "#E2E8F0"}`,
                                     cursor: "pointer" }}
                            data-testid={`onboarding-quickstart-${opt.id}`}>
                      <span className="text-lg md:text-xl font-semibold flex-shrink-0"
                            style={{ color: opt.id === "something_else" ? ORANGE : NAVY }}>
                        {opt.letter})
                      </span>
                      <span className="text-base md:text-lg" style={{ color: opt.id === "something_else" ? ORANGE : "#1E293B" }}>
                        {opt.pre}<span style={{ color: ORANGE }}>{opt.hi}</span>{opt.post}
                      </span>
                      {selected && <Check size={18} className="ml-auto flex-shrink-0" style={{ color: ORANGE }} />}
                    </button>
                  );
                })}
              </div>
              <div className="hidden md:block">
                <img src="/brand/resmo-delivering.png" alt="Resmo holding a tailored resume"
                     style={{ height: 230 }} draggable={false} />
              </div>
            </div>

            {moreOpen && !resumeOpen && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4 rm-fade-up-fast" data-testid="onboarding-more-options">
                {MORE_OPTIONS.map((q) => (
                  <button key={q.id} onClick={() => pickMore(q)}
                          className="text-left p-3 rounded-lg transition-transform hover:-translate-y-0.5"
                          style={{ border: `2px solid ${goal === q.id ? ORANGE : "#E2E8F0"}`,
                                   background: goal === q.id ? "#FFF7ED" : "white" }}
                          data-testid={`onboarding-quickstart-${q.id}`}>
                    <div className="font-bold text-sm" style={{ color: NAVY }}>{q.title}</div>
                    <div className="text-[11px] mt-1" style={{ color: "#64748B" }}>{q.sub}</div>
                  </button>
                ))}
              </div>
            )}

            {resumeOpen && (
              <div className="p-4 rounded-lg mt-4" style={{ border: `2px dashed ${ORANGE}`, background: "#FFFBF5" }}
                   data-testid="onboarding-resume-box">
                {!resumeDone ? (
                  <>
                    <div className="flex gap-2 mb-3">
                      <button onClick={() => setResumeMode("upload")}
                              className={`rm-btn text-xs px-3 py-1.5 ${resumeMode === "upload" ? "rm-btn-dark" : "rm-btn-ghost"}`}
                              data-testid="onboarding-resume-upload-mode">
                        <Upload size={13} /> Upload
                      </button>
                      <button onClick={() => setResumeMode("paste")}
                              className={`rm-btn text-xs px-3 py-1.5 ${resumeMode === "paste" ? "rm-btn-dark" : "rm-btn-ghost"}`}
                              data-testid="onboarding-resume-paste-mode">
                        <ClipboardPaste size={13} /> Paste
                      </button>
                    </div>
                    {resumeMode === "upload" ? (
                      <button onClick={() => fileRef.current?.click()} disabled={chewing}
                              className="w-full py-8 rounded-lg text-sm font-bold"
                              style={{ border: `2px dashed ${NAVY}`, background: "white", color: NAVY }}
                              data-testid="onboarding-resume-dropzone">
                        {chewing ? <Loader size={18} className="animate-spin mx-auto" /> : "Drop or click to upload (PDF, DOCX, TXT)"}
                        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.doc" className="hidden"
                               onChange={onFile} data-testid="onboarding-resume-file-input" />
                      </button>
                    ) : (
                      <>
                        <textarea value={pasted} onChange={(e) => setPasted(e.target.value)}
                                  rows={7} placeholder="Paste your resume text here…"
                                  className="rm-input w-full text-sm" data-testid="onboarding-resume-textarea" />
                        <button onClick={onPaste} disabled={chewing}
                                className="rm-btn rm-btn-primary text-xs mt-2" data-testid="onboarding-resume-save-btn">
                          {chewing ? <Loader size={13} className="animate-spin" /> : <Sparkles size={13} />} Feed it to Resmo
                        </button>
                      </>
                    )}
                    {error && <div className="text-xs font-bold mt-2" style={{ color: "#DC2626" }} data-testid="onboarding-resume-error">{error}</div>}
                  </>
                ) : (
                  <div data-testid="onboarding-resume-done">
                    <div className="flex items-center gap-2 font-bold text-sm mb-2" style={{ color: "#0E5A4A" }}>
                      <Check size={16} /> Got it — here's my first impression:
                    </div>
                    <FirstImpressionCard />
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between mt-6">
              <button onClick={() => (resumeOpen ? setResumeOpen(false) : moreOpen ? setMoreOpen(false) : setStep(1))}
                      className="rm-btn rm-btn-ghost text-xs" data-testid="onboarding-back-2">
                <ArrowLeft size={14} /> Back
              </button>
              {(resumeDone || (goal && goal !== "something_else")) && (
                <button onClick={() => setStep(3)} className="rm-btn rm-btn-primary text-sm"
                        data-testid="onboarding-continue-2">
                  Continue <ArrowRight size={14} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* STEP 3 — Tour offer */}
        {step === 3 && (
          <div className="max-w-2xl mx-auto p-6 md:p-8 text-center" style={card} data-testid="onboarding-step-tour">
            <div className="flex justify-center"><ResmoMascot state="pointing" size={120} /></div>
            <h2 className="font-display text-2xl md:text-3xl uppercase mt-3" style={{ color: NAVY }}>
              Want a quick tour?
            </h2>
            <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: "#475569" }}>
              I'll walk you through the key features — live job search with AI
              matches, ATS help, the authenticity check, your elevator speech
              creator, insight briefings, interview playbooks and lessons learned.
            </p>
            <div className="flex justify-center gap-3 mt-6 flex-wrap">
              <button onClick={() => finish(true)} className="rm-btn rm-btn-primary text-sm px-5"
                      data-testid="onboarding-tour-yes">
                <Map size={15} /> Yes, show me around
              </button>
              <button onClick={() => finish(false)} className="rm-btn rm-btn-ghost text-sm px-5"
                      data-testid="onboarding-tour-no">
                No thanks — let's dive in <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
